import { useEffect, useState } from 'react';

import type { DashboardView } from './dashboard.types';

const hashes: Record<DashboardView, string> = {
  agenda: '#agenda',
  calendar: '#calendario',
  home: '#inicio',
  plan: '#plan',
  quick: '#rapido',
  tools: '#herramientas',
};

export type AppRoute =
  | DashboardView
  | 'account'
  | 'admin'
  | 'banks'
  | 'notifications'
  | 'referrals'
  | 'settings'
  | 'tickets'
  | 'upgrade'
  | 'wallets';

function routeFromHash(hash: string): AppRoute {
  if (hash === '#admin') return 'admin';
  if (hash === '#soporte') return 'tickets';
  if (hash === '#bancos') return 'banks';
  if (hash === '#notificaciones') return 'notifications';
  if (hash === '#referidos') return 'referrals';
  if (hash === '#ajustes') return 'settings';
  if (hash === '#cuenta') return 'account';
  if (hash === '#upgrade') return 'upgrade';
  if (hash === '#wallets') return 'wallets';
  if (hash === hashes.agenda) return 'agenda';
  if (hash === hashes.quick) return 'quick';
  if (hash.startsWith(hashes.tools)) return 'tools';
  if (hash === hashes.plan) return 'plan';
  if (hash === hashes.calendar) return 'calendar';
  return 'home';
}

export function useDashboardNavigation() {
  const [route, setRoute] = useState<AppRoute>(() => routeFromHash(window.location.hash));

  useEffect(() => {
    const syncHash = () => setRoute(routeFromHash(window.location.hash));
    window.addEventListener('hashchange', syncHash);
    return () => window.removeEventListener('hashchange', syncHash);
  }, []);

  const navigate = (nextView: DashboardView) => {
    window.location.hash = hashes[nextView];
    setRoute(nextView);
  };

  const navigateUpgrade = () => {
    window.location.hash = '#upgrade';
    setRoute('upgrade');
  };

  const navigateBanks = () => {
    window.location.hash = '#bancos';
    setRoute('banks');
  };

  const navigateWallets = () => {
    window.location.hash = '#wallets';
    setRoute('wallets');
  };

  const navigateNotifications = () => {
    window.location.hash = '#notificaciones';
    setRoute('notifications');
  };

  const navigateSupport = () => {
    window.location.hash = '#soporte';
    setRoute('tickets');
  };

  const navigateAdmin = () => {
    window.location.hash = '#admin';
    setRoute('admin');
  };

  const navigateAccount = (tab: 'preferences' | 'referrals' | 'sharing' = 'sharing') => {
    const nextRoute =
      tab === 'referrals' ? 'referrals' : tab === 'preferences' ? 'settings' : 'account';
    window.location.hash =
      tab === 'referrals' ? '#referidos' : tab === 'preferences' ? '#ajustes' : '#cuenta';
    setRoute(nextRoute);
  };

  return {
    navigate,
    navigateAdmin,
    navigateAccount,
    navigateBanks,
    navigateNotifications,
    navigateSupport,
    navigateUpgrade,
    navigateWallets,
    route,
    view:
      route === 'upgrade' ||
      route === 'account' ||
      route === 'admin' ||
      route === 'banks' ||
      route === 'notifications' ||
      route === 'referrals' ||
      route === 'settings' ||
      route === 'tickets' ||
      route === 'wallets'
        ? 'home'
        : route,
  };
}
